import React, { Component } from "react";
import Draggable from "react-draggable";
import { connect } from "react-redux";
import { Toggle } from "office-ui-fabric-react/lib-commonjs/Toggle";
import { SpinButton } from "office-ui-fabric-react/lib-commonjs/SpinButton";
import { ComboBox } from "office-ui-fabric-react/lib-commonjs/ComboBox";
import { DefaultButton } from "office-ui-fabric-react/lib-commonjs/Button";
import { ChoiceGroup } from "office-ui-fabric-react/lib/ChoiceGroup";
import { Label } from "office-ui-fabric-react/lib-commonjs/Label";
import {
    floatingToolMoved,
    floatingToolClose,
    setChamberRowOffset,
    setStoneBedVisibility,
    computeStoneBed,
    setStoneBedSeparationDistance,
    setChamberAccesory,
    setChamberUpperCapAccesory,
    setChamberLowerCapAccesory,
    setManifoldIsolatorRowStatus,
} from "../../actions/floating-tool-actions";
import "./index.css";
import {
    CONTEXT_SELECTED_ELEMENT_TYPE_CHAMBER_BED,
    CONTEXT_SELECTED_ELEMENT_TYPE_CHAMBER_ROW,
    CONTEXT_SELECTED_ELEMENT_TYPE_CHAMBER,
    CONTEXT_SELECTED_ELEMENT_TYPE_ENDCAP,
    MSG_INVALID_CONTEXT_ELEMENT_TYPE_ERROR,
    OUTLET_MANIFOLD_ID,
    METRIC,
    IMPERIAL,
    GLOBAL_DRAWING_MODE_INLET_MANIFOLD,
    GLOBAL_DRAWING_MODE_OUTLET_MANIFOLD,
} from "../../lib/types";
import { getManifoldSizing } from "../../lib/manifolds";
import { modalAlertActions } from "../../actions/modal-alert.actions";
import { calculateManifoldSeparations } from "../../actions/manifold-actions";
import ManifoldSelectors from "../../reducers/main-manifold-reducer";
import { cleanChambersByArrayIsolator, modifyChambersByArrayIsolator } from "../../actions/chamber-actions";

const choiceGroupSize = { width: 30, height: 30 };
const MIN_PERIMETER_OFFSET = 12;

class FloatingTool extends Component{
    componentDidUpdate( prevProps ) {
        let names = [
            "chamberRows",
            "chamberType",
            "chamberEndCapType",
            "chamberRowSpacing",
            "sepDistance",
            "underdrain",
            "scalePixels",
            "scaleValue",
        ];

        const hasManifoldsChanged = this.compareProp(prevProps, "mainManifolds");
        const hasManifoldSeparationsChanged = this.compareProp(prevProps, "manifoldSeparations");
        const hasManifoldSizeChanged = prevProps.currentManifold !== this.props.currentManifold;

        if (
            this.anyPropHasChanged(prevProps, names) ||
            hasManifoldsChanged ||
            hasManifoldSeparationsChanged ||
            hasManifoldSizeChanged
        ) {
            this.handleChamberBedContextComputeStoneBed();
        }
    }

    /**
     * This method receives the previous properties object and an Array of
     * the properties to be determined if have changed and returns true if any
     * have changed, or false if all are the same.
     *
     * @param {Object} prevProps The previous properties of the component.
     * @param {Array<string>} propNames The Properties names array.
     */
    anyPropHasChanged( prevProps, propNames ) {
        let changed = false;
        for (let name of propNames) {
            changed = this.props[name] !== prevProps[name] || changed;
        }
        return changed;
    }

    compareProp( prevProps, propKey ) {
        const hasPropChanged = this.anyPropHasChanged(prevProps, [propKey]);

        if (!hasPropChanged) {  // If from a top-level view, the prop hasn't changed, we do not need to dig deeper
            return false;
        }

        let prevProp = prevProps[propKey];
        let prop = this.props[propKey];

        return this._compareProp(propKey, prevProp, prop);
    };

    _compareProp( propKey, prevProp, prop ) {
        if (prevProp instanceof Array && prop instanceof Array) {
            const
                diffLength = prevProp.length !== prop.length,
                diffItems = prop.some(( ignore, index ) => this._compareProp(index, prevProp[index], prop[index]));

            return diffLength || diffItems;
        } else if (prevProp instanceof Object && prop instanceof Object) {
            const prevPropKeys = Object.keys(prevProp);
            const propKeys = Object.keys(prop);

            const
                diffLength = prevPropKeys.length !== propKeys.length,
                diffItems = propKeys.some(_propKey => this._compareProp(_propKey, prevProp[_propKey], prop[_propKey]));

            return diffLength || diffItems;
        }

        const diffItem = prevProp !== prop;

        return diffItem;
    }

    handleDragStop( event, node ) {
        if (this.props.x !== node.x || this.props.y !== node.y) {
            this.props.floatingToolMoved(node.x, node.y);
        }
    }

    handleCloseClick() {
        this.props.floatingToolClose();
    }

    computeUnderdrainSeparationDistance( stoneBedSeparation ) {
        /* minimun separation distance is 1.6 feets */
        let minDist = 1.6;
        /* calculate the additional separation distance */
        let additionalSeparation =
            stoneBedSeparation / 12 >= minDist
                ? 0
                : minDist - stoneBedSeparation / 12;

        return (
            this.props.scalePixels * ( additionalSeparation / this.props.scaleValue )
        );
    }

    computeSeparationDistance( separation ) {
        return this.props.scalePixels * ( separation / 12 / this.props.scaleValue );
    }

    handleStoneBedContextSeparationChange( change ) {
        /* get scale feet unit */
        change = parseFloat(change);
        if (this.props.unit === METRIC) {
            change = change > 0 ? 1 : -1;
        }
        let separation = this.props.sepDistance;
        let parsedSeparation = Math.round(parseFloat(this.convertUnits(separation, "in")) * 100) / 100;
        parsedSeparation += change;
        /* if separation distance is not valid, return */
        if (parsedSeparation < this.convertUnits2(MIN_PERIMETER_OFFSET, "in")) {
            return;
        }
        let sepImperial = this.convertUnits2(parsedSeparation, "in");
        let sepDistancePixels = this.computeSeparationDistance(sepImperial);
        /* default separation feets for underdrain */
        let underDrainSepDistancePixels = this.computeUnderdrainSeparationDistance(
            sepImperial,
        );
        /* invoke actions */
        this.props.setStoneBedSeparationDistance(
            sepImperial,
            sepDistancePixels,
            this.props.chamberRows,
            this.props.chamberRowSpacingPixels,
            underDrainSepDistancePixels,
            this.props.underdrain,
            this.props.manifoldSeparations,
            this.props.upperManifolds,
            this.props.lowerManifolds,
        );
        return String(this.convertUnitsForDisplay(parsedSeparation, "in"));
    }

    handleStoneBedContextSeparationOnValidate( separation ) {
        if (isNaN(separation)) {
            return `${this.convertUnits(this.props.sepDistance, "in")}`;
        }
        /* get the integer value */
        separation = parseFloat(separation);
        let sepImperial = this.convertUnits2(separation, "in");
        /* if less than zero, illegal separation distance */
        if (sepImperial < MIN_PERIMETER_OFFSET) {
            return `${this.convertUnits(this.props.sepDistance, "in")}`;
        }
        /* calculate separation pixels */
        let sepDistancePixels = this.computeSeparationDistance(sepImperial);
        let manifoldSeparations = this.props.manifoldSeparations;
        /* default separation feets for underdrain in feets */
        let underDrainSepDistancePixels = this.computeUnderdrainSeparationDistance(
            sepImperial,
        );
        /* invoke actions */
        this.props.setStoneBedSeparationDistance(
            sepImperial,
            sepDistancePixels,
            this.props.chamberRows,
            this.props.chamberRowSpacingPixels,
            manifoldSeparations,
            underDrainSepDistancePixels,
            this.props.underdrain,
            this.props.upperManifolds,
            this.props.lowerManifolds,
        );
    }

    handleChamberBedContextComputeStoneBed() {
        /* calculate separation pixels */
        let sepDistancePixels = this.computeSeparationDistance(
            this.props.sepDistance,
        );
        /* default separation feets for underdrain is 0.6 feets */
        let underDrainSepDistancePixels = this.computeUnderdrainSeparationDistance(
            this.props.sepDistance,
        );

        /* invoke compute action */
        this.props.computeStoneBed(
            this.props.chamberRows,
            this.props.chamberRowSpacingPixels,
            sepDistancePixels,
            underDrainSepDistancePixels,
            this.props.underdrain,
            this.props.manifoldSeparations,
            this.props.upperManifolds,
            this.props.lowerManifolds,
        );
    }

    getScaledValue( value ) {
        return this.props.scalePixels * ( value / this.props.scaleValue );
    }

    handleChamberBedContextShowHideStoneBed() {
        this.props.setStoneBedVisibility();
    }

    handleRowContextOffsetChange( row, change ) {
        /* get scale feet unit */
        let offset = parseFloat(row.offSet);
        let parsedOffset = parseFloat(this.convertUnits2(offset, "ft"));
        parsedOffset += parseFloat(change);
        /* calculate pixelOffset */
        let scaleOffset = offset + parseFloat(this.convertUnits2(parseFloat(change), "ft"));
        let pixelOffSet = this.props.scalePixels * ( scaleOffset / this.props.scaleValue );
        /* invoke actions */
        this.props.setChamberRowOffset(row.row, scaleOffset, pixelOffSet);

        return `${parsedOffset}`;
    }

    handleRowContextOffsetOnValidate( row, offSet ) {
        // const isMetric = this.props.unit === METRIC;
        if (isNaN(offSet)) {
            return `${this.convertUnits2(row.offSet, "ft")}`;
        }
        /* get the integer value */
        let convertedOffset = parseFloat(this.convertUnits2(offSet, "ft"));
        /* calculate pixelOffset */
        let pixelOffSet = this.props.scalePixels * ( convertedOffset / this.props.scaleValue );
        /* invoke actions */
        this.props.setChamberRowOffset(row.row, convertedOffset, pixelOffSet);
        return String(this.convertUnitsForDisplay(convertedOffset));
    }

    getInletOutletCounters() {
        return this.props.chamberRows
            .map(r => r.getInletOutletCount())
            .reduce(( prev, next ) => {
                return {
                    inlet: prev.inlet + next.inlet,
                    outlet: prev.outlet + next.outlet,
                };
            });
    }

    convertUnits( val, unitType ) {
        let unitValue = 0;
        switch (this.props.unit) {
            case METRIC:
                switch (unitType) {
                    case "ft": // Feets to meters
                        unitValue = parseFloat(Math.round(( val * 0.3048 ) * 100) / 100).toFixed(2);
                        break;
                    case "in":
                        unitValue = Math.round(Math.round(( val * 25.4 ) * 100) / 100);
                        break;
                    default:
                        unitValue = Math.round(val * 100) / 100;
                        break;
                }
                break;
            case IMPERIAL:
                unitValue = val;
                break;
            default:
                break;
        }
        return unitValue;
    }

    convertUnits2( value, unitType ) {
        let unitValue = 0;
        switch (this.props.unit) {
            case METRIC:
                switch (unitType) {
                    case "ft": // Feets to meters
                        unitValue = parseFloat(Math.round(( value / 0.3048 ) * 100) / 100).toFixed(2);
                        break;
                    case "in": // Inches to milimeters
                        unitValue = Math.round(( value / 25.4 ) * 100) / 100;
                        break;
                    case "cf": // Cubic feets to cubic meters
                        unitValue = parseFloat(Math.round(( value * 35.315 ) * 100) / 100).toFixed(2);
                        break;
                    default:
                        unitValue = Math.round(value * 100) / 100;
                        break;
                }
                break;
            case IMPERIAL:
                unitValue = value;
                break;
            default:
                break;
        }
        return unitValue;
    }

    convertUnitsForDisplay = ( value, unitType ) => {
        let unitValue = 0;
        switch (this.props.unit) {
            case METRIC:
                switch (unitType) {
                    case "ft": // Feets to meters
                        unitValue = parseFloat(Math.round(( value * 0.3048 ) * 100) / 100).toFixed(2);
                        break;
                    case "in": // Inches to milimeters
                        unitValue = Math.round(Math.round(( value * 25.4 ) * 100) / 100);
                        break;
                    case "cf": // Cubic feets to cubic meters
                        unitValue = parseFloat(Math.round(( value / 35.315 ) * 100) / 100).toFixed(2);
                        break;
                    default:
                        unitValue = Math.round(value * 100) / 100;
                        break;
                }
                break;
            case IMPERIAL:
                unitValue = value;
                break;
            default:
                break;
        }
        return unitValue;
    };

    renderChamberTools() {
        /* the for object */
        const forComponent = {
            endcaps: true,
            chambers: true,
        };
        /* the accesories map */
        const aMap = new Map();
        /* get the accesories */
        let accesories = this.props.accessories
            .map(a => {
                /* adding to map */
                aMap.set(a.id, a);
                /* return the options for combo box */
                return {
                    key: a.id,
                    text: this.props.content[this.props.lang][( a.localeKey || a.name )],
                    for: a.for,
                    order: a.order,
                    variant: a.variant,
                    exceptions: a.exceptions,
                };
            })
            .sort(( a, b ) => a.order - b.order);
        /* add the none option */
        accesories.push({
            key: "none",
            text: this.props.content[this.props.lang].none,
            for: forComponent,
            exceptions: {},
        });
        /* this current chamber data */
        let row = this.props.chamberRows[this.props.contextSelectedElement.row];
        /* the selected chamber */
        let cmb = row.chambers[this.props.contextSelectedElement.col];

        /* if chamber is null return */
        if (!cmb) {
            /* close the tool */
            this.handleCloseClick();
            return <div/>;
        }
        /* remove all inlet and outlet from collection if it is an interior chamber */
        if (row.isInteriorChamber(cmb.col)) {
            /* filter all inlet and outlets */
            accesories = accesories.filter(
                a => !a.key.toLowerCase().match(/(inlet|outlet)/g),
            );
        }
        /* the inlet/outlet counters in this chamber bed */
        let inletOutletCounters = this.getInletOutletCounters();
        /* remove all inlet accessories from options if more than 1 present and this
        chamber is not the one with it attached */
        // NOTE: all allowed upon request from ADS
        if (
            false &&
            inletOutletCounters.inlet > 0 &&
            cmb.getInletOutletCount().inlet === 0
        ) {
            /* filter all inlets */
            accesories = accesories.filter(
                a => !a.key.toLowerCase().match(/(inlet)/g),
            );
        }
        /* remove all outlet accessories from options if more than 1 present and this
        chamber is not the one with it attached */
        // NOTE: all allowed upon request from ADS
        if (
            false &&
            inletOutletCounters.outlet > 0 &&
            cmb.getInletOutletCount().outlet === 0
        ) {
            /* filter all outlets */
            accesories = accesories.filter(
                a => !a.key.toLowerCase().match(/(outlet)/g),
            );
        }

        /* apply exceptions based on this chamber type */
        accesories = accesories.filter(a => {
            let ex = a.exceptions[this.props.chamberType.id];
            /* check if this accessory must be filtered */
            return !( ex && !ex.attachable );
        });

        /* endcap accessories */
        let endcapAccesories = accesories.filter(a => a.for.endcaps);
        /* render floating window */
        return (
            <div className="chamberFloatingOptions">
                <span>
                    {this.props.content[this.props.lang].chamber} <strong>{`${cmb.row} - ${cmb.col}`}</strong>
                </span>
                <hr/>
                <ComboBox
                    selectedKey={
                        cmb.accessory && cmb.accessory.id ? cmb.accessory.id : "none"
                    }
                    label={this.props.content[this.props.lang].chamberAccessory}
                    id="chamberAccesory"
                    ariaLabel={this.props.content[this.props.lang].chamberAccessory}
                    options={accesories.filter(a => a.for.chambers)}
                    onChanged={value => {
                        /* call the set accessory action */
                        this.props.setChamberAccesory(cmb.row, cmb.col, {
                            ...aMap.get(value.key),
                        });
                    }}
                />
                <br/>
                <hr/>
                <span>{this.props.content[this.props.lang].upperEndCap}</span>
                <ComboBox
                    disabled={!cmb.upperCapActive || endcapAccesories.length === 1}
                    selectedKey={
                        cmb.upperCapAccessory && cmb.upperCapAccessory.id
                            ? cmb.upperCapAccessory.id
                            : "none"
                    }
                    label={this.props.content[this.props.lang].accessory}
                    id="upperEndCapAccesory"
                    ariaLabel={this.props.content[this.props.lang].upperEndCapAccessory}
                    options={endcapAccesories}
                    onChanged={value => {
                        let structureType = getManifoldSizing(this.props.totalStorageProvided, this.props.chamberType).inletStructureType;
                        if (value.key === OUTLET_MANIFOLD_ID) {
                            structureType = getManifoldSizing(this.props.totalStorageProvided, this.props.chamberType).outletStructureType;
                        }
                        /* call the set accesory action */
                        this.props.setChamberUpperCapAccesory(cmb.row, cmb.col, {
                            ...aMap.get(value.key),
                            variant: structureType,
                        });
                    }}
                />
                <br/>
                <ChoiceGroup
                    className={
                        cmb.upperCapAccessory &&
                        cmb.upperCapAccessory.id &&
                        cmb.upperCapAccessory.id.toLowerCase().match(/(inlet|outlet)/g)
                            ? "structureTypeChoiceGroup"
                            : "structureTypeChoiceGroupHidden"
                    }
                    selectedKey={
                        cmb.upperCapAccessory ? cmb.upperCapAccessory.variant : "none"
                    }
                    options={[
                        {
                            key: "round",
                            iconProps: { iconName: "StatusCircleOuter" },
                            imageAlt: "Round Structure",
                            imageSize: choiceGroupSize,
                            text: this.props.content[this.props.lang].round,
                        },
                        {
                            key: "square",
                            iconProps: { iconName: "SquareShapeSolid" },
                            imageAlt: "square Structure",
                            imageSize: choiceGroupSize,
                            text: this.props.content[this.props.lang].square,
                        },
                        {
                            key: "nyloplast",
                            iconProps: { iconName: "StatusCircleRing" },
                            imageAlt: "Nyloplast Structure",
                            imageSize: choiceGroupSize,
                            text: "nyloplast",
                        },
                    ]}
                    onChange={( evt, option ) => {
                        /* update the accessory with choosed variant */
                        let accessory = { ...cmb.upperCapAccessory, variant: option.key };
                        /* call the set accesory action */
                        this.props.setChamberUpperCapAccesory(cmb.row, cmb.col, accessory);
                    }}
                />
                <hr/>
                <br/>
                <span>{this.props.content[this.props.lang].lowerEndCap}</span>
                <ComboBox
                    disabled={!cmb.lowerCapActive || endcapAccesories.length === 1}
                    selectedKey={
                        cmb.lowerCapAccessory && cmb.lowerCapAccessory.id
                            ? cmb.lowerCapAccessory.id
                            : "none"
                    }
                    label={this.props.content[this.props.lang].accessory}
                    id="lowerEndCapAccesory"
                    ariaLabel={this.props.content[this.props.lang].lowerEndCapAccesory}
                    options={endcapAccesories}
                    onChanged={value => {
                        let structureType = getManifoldSizing(this.props.totalStorageProvided, this.props.chamberType).inletStructureType;
                        if (value.key === OUTLET_MANIFOLD_ID) {
                            structureType = getManifoldSizing(this.props.totalStorageProvided, this.props.chamberType).outletStructureType;
                        }

                        this.props.setChamberLowerCapAccesory(cmb.row, cmb.col, {
                            ...aMap.get(value.key),
                            variant: structureType,
                        });
                    }}
                />
                <br/>
                <ChoiceGroup
                    className={
                        cmb.lowerCapAccessory &&
                        cmb.lowerCapAccessory.id &&
                        cmb.lowerCapAccessory.id.toLowerCase().match(/(inlet|outlet)/g)
                            ? "structureTypeChoiceGroup"
                            : "structureTypeChoiceGroupHidden"
                    }
                    selectedKey={
                        cmb.lowerCapAccessory ? cmb.lowerCapAccessory.variant : "none"
                    }
                    options={[
                        {
                            key: "round",
                            iconProps: { iconName: "StatusCircleOuter" },
                            imageAlt: "Round Structure",
                            imageSize: choiceGroupSize,
                            text: this.props.content[this.props.lang].round,
                        },
                        {
                            key: "square",
                            iconProps: { iconName: "SquareShapeSolid" },
                            imageAlt: "square Structure",
                            imageSize: choiceGroupSize,
                            text: this.props.content[this.props.lang].square,
                        },
                        {
                            key: "nyloplast",
                            iconProps: { iconName: "StatusCircleRing" },
                            imageAlt: "Nyloplast Structure",
                            imageSize: choiceGroupSize,
                            text: "nyloplast",
                        },
                    ]}
                    onChange={( evt, option ) => {
                        /* update the accessory with choosed variant */
                        let accessory = { ...cmb.lowerCapAccessory, variant: option.key };
                        /* call the set accesory action */
                        this.props.setChamberLowerCapAccesory(cmb.row, cmb.col, accessory);
                    }}
                />
            </div>
        );
    }

    renderEndCapTools() {
    }

    renderChamberBedTools() {
        /* return the rendered options */
        return (
            <div>
                <br/>
                <DefaultButton
                    primary={true}
                    data-automation-id="compute-stone-bed"
                    text={this.props.content[this.props.lang].computeStoneBed}
                    onClick={() => this.handleChamberBedContextComputeStoneBed()}
                />
                <br/>
                <Toggle
                    className="Toggle"
                    checked={this.props.stoneBedVisible}
                    onText={this.props.content[this.props.lang].showStoneBed}
                    offText={this.props.content[this.props.lang].hideStoneBed}
                    onClick={() => this.handleChamberBedContextShowHideStoneBed()}
                />
                <div className="separationDistanceSpinner">
                    <Label>Sep&nbsp;</Label>
                    <SpinButton
                        value={`${this.convertUnits(this.props.sepDistance, "in")}`}
                        onIncrement={this.handleStoneBedContextSeparationChange.bind(
                            this,
                            0.5,
                        )}
                        onDecrement={this.handleStoneBedContextSeparationChange.bind(
                            this,
                            -0.5,
                        )}
                        onValidate={this.handleStoneBedContextSeparationOnValidate.bind(
                            this,
                        )}
                        min={"0"}
                        step={0.5}
                    />
                    <Label>&nbsp;{this.props.lbl[this.props.unit].in[this.props.lang]}</Label>
                </div>
            </div>
        );
    }

    renderChamberRowTools() {
        /* the selected chamber row */
        let row = this.props.chamberRows[this.props.contextSelectedElement.row];

        /* return the rendered options */
        return (
            <div>
                <br/>
                <div className="offSetSpinner">
                    <Label>{this.props.content[this.props.lang].offset}:&nbsp;</Label>
                    <SpinButton
                        value={`${this.convertUnits(row.offSet, "ft")}`}
                        onIncrement={this.handleRowContextOffsetChange.bind(this, row, 0.5)}
                        onDecrement={this.handleRowContextOffsetChange.bind(
                            this,
                            row,
                            -0.5,
                        )}
                        onValidate={this.handleRowContextOffsetOnValidate.bind(this, row)}
                        min={"0"}
                    />
                    <Label>&nbsp;{this.props.lbl[this.props.unit].ft[this.props.lang]}</Label>
                </div>
            </div>
        );
    }

    renderToolContent() {
        /* the tool dynamic body render */
        let body;

        /* check if tool is out of bounds */
        if (
            this.props.contextSelectedElement.row >= this.props.chamberRows.length
        ) {
            /* close the tool */
            this.handleCloseClick();
            return <div/>;
        }

        /* decide witch body to render depending on targetType */
        switch (this.props.contextSelectedElement.type) {
            case CONTEXT_SELECTED_ELEMENT_TYPE_CHAMBER:
                body = this.renderChamberTools();
                break;
            case CONTEXT_SELECTED_ELEMENT_TYPE_ENDCAP:
                body = this.renderEndCapTools();
                break;
            case CONTEXT_SELECTED_ELEMENT_TYPE_CHAMBER_ROW:
                body = this.renderChamberRowTools();
                break;
            case CONTEXT_SELECTED_ELEMENT_TYPE_CHAMBER_BED:
                body = this.renderChamberBedTools();
                break;
            default:
                throw new Error(MSG_INVALID_CONTEXT_ELEMENT_TYPE_ERROR);
        }
        /* render the floating tools */
        return (
            <div className="FLoatingTool">
                <div className="handle">
                    <span
                        className="closeButtom"
                        onClick={this.handleCloseClick.bind(this)}
                    >
                        X
          </span>
                </div>
                {body}
            </div>
        );
    }

    render() {
        if (this.props.contextSelectedElement
            && this.props.drawingMode !== GLOBAL_DRAWING_MODE_INLET_MANIFOLD
            && this.props.drawingMode !== GLOBAL_DRAWING_MODE_OUTLET_MANIFOLD) {
            return (
                <Draggable
                    onStop={this.handleDragStop.bind(this)}
                    bounds="#layout"
                    handle=".handle"
                    position={{ x: this.props.x, y: this.props.y }}
                >
                    {this.renderToolContent()}
                </Draggable>
            );
        } else {
            return <span style={{ display: "none" }}/>;
        }
    }
}


const mapStateToProps = state => {
    return {
        x: state.floatingtool.x,
        y: state.floatingtool.y,

        lbl: state.localization.lbl,
        unit: state.localization.unit,
        lang: state.localization.lang,
        content: state.localization.content,
        scaleValue: state.globals.scaleValue,
        scalePixels: state.globals.scalePixels,
        contextSelectedElement: state.globals.contextSelectedElement,
        totalStorageProvided: state.calculations.totalStorageProvided,
        chamberRowSpacingPixels: state.globals.chamberRowSpacingPixels,

        chamberType: state.globals.chamberType,
        chamberEndCapType: state.globals.chamberEndCapType,

        chamberRows: state.chamberbed.chamberRows,
        chamberbed: state.chamberbed,
        stoneBedVisible: state.stonebed.visible,
        sepDistance: state.stonebed.sepDistance,
        accessories: state.apptoolbar.accessories,
        underdrain: state.calculations.underdrain,

        upperManifolds: ManifoldSelectors.getManifoldsByType("upper"),
        lowerManifolds: ManifoldSelectors.getManifoldsByType("lower"),
        mainManifolds: state.mainManifolds.manifolds,

        upperManifoldSizing: ManifoldSelectors.getManifoldsSizingByTypeAndProperty("upper", "manifoldSizing"),
        lowerManifoldSizing: ManifoldSelectors.getManifoldsSizingByTypeAndProperty("lower", "manifoldSizing"),

        manifoldSeparations: state.calculations.manifoldSeparations,
        drawingMode: state.globals.drawingMode,
        currentManifold: state.manifoldEntry.currentManifold,
    };
};

const mapDispatchToProps = {
    computeStoneBed,
    floatingToolMoved,
    floatingToolClose,
    modalAlertActions,
    setChamberAccesory,
    setChamberRowOffset,
    setStoneBedVisibility,
    setChamberUpperCapAccesory,
    setChamberLowerCapAccesory,
    calculateManifoldSeparations,
    setStoneBedSeparationDistance,
    modifyChambersByArrayIsolator,
    cleanChambersByArrayIsolator,
    setManifoldIsolatorRowStatus,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(FloatingTool);



// WEBPACK FOOTER //
// ./src/components/FloatingTool/index.js